import { Link } from "react-router-dom";
import {
  Boxes, ScanText, FileSignature, ExternalLink, Wrench, BarChart3,
  UserPlus, Shield, Layers, MapPin, FlaskConical, Building2, Droplets,
  PackageCheck, Image as ImageIcon, Bug as BugIcon,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { EntityShell } from "../components/shell";

const TOOLS = [
  // ── Field operations ─────────────────────────────────────────────────
  {
    to: "/equipment",
    label: "Equipment & Inventory",
    desc: "Stock master, asset tracking, service history, parts catalogue.",
    icon: Wrench,
    perm: "nav.equipment",
    accent: "#1FB6C1",
  },
  {
    to: "/picking",
    label: "Picking",
    desc: "Pick lists for site visits — generated from upcoming job cards.",
    icon: PackageCheck,
    perm: "nav.tools",
    accent: "#F59E0B",
  },
  {
    to: "/trips",
    label: "Plan a trip",
    desc: "Best-in-industry trip planner — pick stops, auto-generate sales briefs, sync to calendar + tasks.",
    icon: MapPin,
    perm: "nav.leads",
    accent: "#1FB6C1",
  },
  // ── Sales / onboarding ───────────────────────────────────────────────
  {
    to: "/tools/onboarding?new=prospect",
    label: "Lead Onboarding",
    desc: "Launch the detailed water source, power, occupancy & filtration questionnaire for a new lead.",
    icon: UserPlus,
    perm: "tools.onboarding",
    accent: "#F59E0B",
  },
  {
    to: "/tools/water-info-sheet",
    label: "Water Purification Info Sheet",
    desc: "Lean sizing fact-find. Share a public link with a prospect — completed sheets land in the app with a notification.",
    icon: Droplets,
    perm: "tools.onboarding",
    accent: "#1FB6C1",
  },
  {
    to: "/suppliers",
    label: "Suppliers",
    desc: "Manufacturers, distributors, service partners. Documents, trading terms, linked inventory & datasheets. Admin / Manager only.",
    icon: Building2,
    perm: "nav.suppliers",
    roles: ["admin", "manager"],
    accent: "#F59E0B",
  },
  // ── Research / intelligence ──────────────────────────────────────────
  {
    to: "/tools/engineering-desk",
    label: "Engineering Desk",
    desc: "Deep-reasoning research sessions with Hidro — fault-finding, chemistry, hydraulics, RO. Resolved sessions become a shared library.",
    icon: FlaskConical,
    perm: "nav.tools",
    accent: "#1FB6C1",
  },
  {
    to: "/tools/analytics",
    label: "Analytics",
    desc: "Sales pipeline, field performance, support SLAs, workflow health — live.",
    icon: BarChart3,
    perm: "tools.analytics",
    accent: "#A78BFA",
  },
  {
    to: "/gallery",
    label: "Gallery",
    desc: "Photo & video evidence library — searchable across customers, jobs and tickets.",
    icon: ImageIcon,
    perm: "nav.tools",
    accent: "#10B981",
  },
  // ── Stock / catalogue ────────────────────────────────────────────────
  {
    to: "/procurement",
    label: "Procurement",
    desc: "Track supplier POs, deliveries and GRVs. Mirror of the picking workflow for goods coming in.",
    icon: Boxes,
    perm: "nav.tools",
    accent: "#22d3ee",
  },
  {
    to: "/tools/inventory-categories",
    label: "Catalogue category editor",
    desc: "Re-bucket inventory items so the storefront groups them correctly.",
    icon: Layers,
    perm: "nav.tools",
    accent: "#1FB6C1",
  },
  // ── Finance docs ─────────────────────────────────────────────────────
  {
    to: "/ocr/invoices",
    label: "Invoice OCR",
    desc: "Snap a supplier invoice and AI extracts line-items + creates equipment.",
    icon: ScanText,
    perm: "tools.ocr",
    accent: "#22d3ee",
  },
  {
    to: "/account-applications",
    label: "Account Application",
    desc: "Review account applications, approve / reject, download PDF.",
    icon: FileSignature,
    perm: "action.manage_account_applications",
    accent: "#22d3ee",
  },
  {
    to: "/apply/account",
    label: "Public Account Application link",
    desc: "Open or share the public account-application form with prospects.",
    icon: ExternalLink,
    external: true,
    perm: "tools.onboarding",
    accent: "#22d3ee",
  },
  // ── Admin ────────────────────────────────────────────────────────────
  // Iter 228 — "Users & Roles" moved out of Tools to /settings/team.
  // Tools workshop is now reserved for power-user workflows; user/role
  // administration lives in Settings where admins expect to find it.
  {
    to: "/settings/hidro/health",
    label: "Bug reports & health",
    desc: "Triage console — recent bug reports, AI confidence dips, slow endpoints.",
    icon: BugIcon,
    perm: "tools.users_manager",
    roles: ["owner", "admin"],
    accent: "#F43F5E",
  },
];

export default function ToolsIndex() {
  const { hasPermission, user } = useAuth();
  const visible = TOOLS.filter((t) => {
    if (hasPermission(t.perm)) return true;
    if (Array.isArray(t.roles) && t.roles.includes(user?.role)) return true;
    return false;
  });
  return (
    <EntityShell kindLabel="Tools" title="Tools workshop" backTo="/">
      <div className="space-y-4" data-testid="tools-index">
        <p className="text-sm text-[#a0aabf] max-w-3xl">
          Power features grouped in one place — field operations, sales onboarding,
          research, stock control, finance docs and admin.  Each card is a
          deep-link into the relevant module.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          {visible.map((t) => {
            const accent = t.accent || "#22d3ee";
            const Icon = t.icon;
            const commonCls = "hv-card p-4 flex items-start gap-3 transition group";
            const style = { "--tool-accent": accent };
            const body = (
              <>
                <Icon className="w-5 h-5 mt-0.5 shrink-0" style={{ color: accent }} />
                <div className="min-w-0">
                  <div className="font-medium text-sm flex items-center gap-1">
                    {t.label}
                    {t.external && <ExternalLink className="w-3 h-3 text-[#647087] opacity-0 group-hover:opacity-100 transition" />}
                  </div>
                  <div className="text-xs text-[#647087] mt-1 leading-relaxed">{t.desc}</div>
                </div>
              </>
            );
            return t.external ? (
              <a
                key={t.to}
                href={t.to}
                target="_blank"
                rel="noreferrer"
                data-testid={`tools-card-${t.to.replace(/\W+/g, "-")}`}
                style={style}
                className={`${commonCls} hover:border-[color:var(--tool-accent)]/50`}
              >{body}</a>
            ) : (
              <Link
                key={t.to}
                to={t.to}
                data-testid={`tools-card-${t.to.replace(/\W+/g, "-")}`}
                style={style}
                className={`${commonCls} hover:border-[color:var(--tool-accent)]/50`}
              >{body}</Link>
            );
          })}
        </div>
      </div>
    </EntityShell>
  );
}
